import React from 'react';
import { motion, Variants } from 'framer-motion';

interface LoadingProps {
  message?: string;
  showProgress?: boolean;
}

const Loading: React.FC<LoadingProps> = ({ 
  showProgress = true, 
}) => {

  // Animation variants
  const containerVariants : Variants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        staggerChildren: 0.2
      }
    }
  };

  const logoVariants : Variants = {
    initial: { 
      scale: 0.5, 
      opacity: 0,
      y: 20
    },
    animate: { 
      scale: 1, 
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
        duration: 0.8
      }
    }
  };

  const pulseVariants : Variants = {
    animate: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const textVariants : Variants = {
    initial: { 
      opacity: 0, 
      y: 10 
    },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.3,
        ease: "easeOut"
      }
    }
  };

  const progressBarVariants : Variants = {
    initial: { scaleX: 0 },
    animate: {
      scaleX: [0, 0.7, 0.3, 1, 0.8, 1],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const glowVariants : Variants = {
    animate: {
      opacity: [0.5, 0.8, 0.5],
      scale: [1, 1.1, 1],
      transition: {
        duration: 2.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      {/* Animated background pattern */}
      <motion.div 
        className="absolute inset-0 opacity-5"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.05 }}
        transition={{ duration: 1.5 }}
      >
        <div 
          className="h-full w-full"
          style={{
            backgroundImage: `
              radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgba(147, 51, 234, 0.08) 0%, transparent 50%)
            `
          }}
        />
      </motion.div>

      {/* Main loading container */}
      <motion.div 
        className="relative flex flex-col items-center space-y-6 p-8"
        variants={containerVariants}
        initial="initial"
        animate="animate"
      >
        {/* Logo container with animations */}
        <div className="relative">
          {/* Glow effect behind logo */}
          <motion.div 
            className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/20 via-purple-500/10 to-blue-500/20 blur-xl"
            variants={glowVariants}
            animate="animate"
            style={{
              width: '140px',
              height: '140px',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          />

          {/* Logo with bounce and pulse animation */}
          <motion.div 
            className="relative"
            variants={logoVariants}
            initial="initial"
            animate="animate"
          >
            <motion.div 
              className={`relative overflow-hidden rounded-2xl`}
              variants={pulseVariants}
              animate="animate"
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
            >
              
              {/* Logo image */}
              <motion.img
                src="/assets/images/logo.png"
                alt="University Logo"
                className="w-48 object-contain p-3 relative z-10"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ 
                  scale: 1, 
                  opacity: 1,
                  transition: { 
                    delay: 0.5,
                    duration: 0.6,
                    ease: "easeOut"
                  }
                }}
              />
              
              {/* Shine effect */}
              <motion.div 
                className="absolute -top-4 -left-4 h-16 w-4 bg-gradient-to-b from-transparent via-white/40 to-transparent rotate-12"
                animate={{
                  x: [-20, 100],
                  opacity: [0, 1, 0],
                  transition: {
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 1,
                    ease: "easeInOut"
                  }
                }}
              />
            </motion.div>
          </motion.div>
        </div>

        {/* Loading text */}
        <motion.div 
          className="text-center space-y-4"
          variants={textVariants}
          initial="initial"
          animate="animate"
        >
          {/* <motion.h3 
            className={`font-semibold text-gray-700`} 
            dir="rtl"
            animate={{
              opacity: [0.7, 1, 0.7],
              transition: { duration: 2, repeat: Infinity }
            }}
          >
            {message}
          </motion.h3> */}
          
          {showProgress && (
            <>
              {/* Progress bar */}
              <motion.div 
                className="w-48 md:w-64 h-1 bg-gray-200 rounded-full overflow-hidden mx-auto"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
              >
                <motion.div 
                  className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 rounded-full origin-left"
                  variants={progressBarVariants}
                  initial="initial"
                  animate="animate"
                />
              </motion.div>
            </>
          )}
        </motion.div>

        {/* University info */}
        <motion.div 
          className="text-center text-xs text-gray-500 space-y-1"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
        >
          <motion.p
            animate={{
              opacity: [0.6, 1, 0.6],
              transition: { duration: 3, repeat: Infinity }
            }}
          >
            نظام إدارة الإنجازات الأكاديمية
          </motion.p>
          <motion.p
            animate={{
              opacity: [0.4, 0.8, 0.4],
              transition: { duration: 3, repeat: Infinity, delay: 0.5 }
            }}
          >
            جامعة جنوب الوادي
          </motion.p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Loading;